import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { Skeleton } from '@material-ui/lab';
import { ChartRangeItem } from '~/modules/common/charts/timeline/components';
import { ResourcePlanAllocationTotal } from '~/modules/resourcing/components';
import { useMeContext } from '~/modules/me/useMeContext';
import { deepPure } from '~/util';
import { useResourceRequestToolbarContext } from '~/modules/projects/resourcing-plan/hooks';

import useStyles from './useStyles';
import ResourceRequestChartActions from './ResourceRequestChartActions';
import { useProjectTotals } from './hooks/useProjectTotals';

export const ResourceRequestChartFooter = ({
  scale,
  chartDates,
  leftComponentWidth,
  project,
  handleAddResourceRequestClick,
  handleAddAllocation
}) => {
  const { permittedActionUris } = project;
  const classes = useStyles();

  const me = useMeContext();

  const { permissionsMap } = me;

  const { filter } = useResourceRequestToolbarContext();
  const {
    loading,
    allocationsTotals,
    resourceRequestsTotals
  } = useProjectTotals({
    filter
  });

  const isResourceAssignmentEnabled = Boolean(
    permissionsMap['urn:replicon-webui:completed-resource-allocation:edit']
  );

  const canViewCostData = permittedActionUris.includes(
    'urn:replicon:project-action:view-cost-data'
  );

  const total = useMemo(() => {
    if (loading) {
      return <Skeleton height={18} width="90%" />;
    }
    if (!allocationsTotals && !resourceRequestsTotals) {
      return null;
    }

    const { totalCost: totalAllocatedCosts, totalHours: totalAllocatedHours } =
      allocationsTotals || {};

    const { totalCost: totalRequestedCosts, totalHours: totalRequestedHours } =
      resourceRequestsTotals || {};

    // eslint-disable-next-line react-perf/jsx-no-new-array-as-prop
    const totalCosts = totalAllocatedCosts
      ? [
          {
            currencySymbol: totalAllocatedCosts.currency.symbol,
            amount: Math.max(
              0,
              (totalAllocatedCosts.amount || 0) +
                (totalRequestedCosts.amount || 0)
            )
          }
        ]
      : null;

    const totalHours =
      Math.max(0, totalAllocatedHours || 0) +
      Math.max(0, totalRequestedHours || 0);

    return (
      totalCosts &&
      totalHours && (
        <ResourcePlanAllocationTotal
          qeIdTag="Total"
          totalHours={totalHours}
          totalCosts={totalCosts}
          hideCosts={!canViewCostData}
        />
      )
    );
  }, [loading, canViewCostData, allocationsTotals, resourceRequestsTotals]);

  const cssClasses = useMemo(
    () => ({
      itemContainer: classes.container,
      timeContainer: classes.container,
      totalContainer: classes.container,
      root: classes.root
    }),
    [classes.container, classes.root]
  );

  const leftComponent = useMemo(
    () => (
      <ResourceRequestChartActions
        handleAddResourceRequestClick={handleAddResourceRequestClick}
        isResourceAssignmentEnabled={isResourceAssignmentEnabled}
        isEditProjectAllowed={permittedActionUris.includes(
          'urn:replicon:project-action:edit-project'
        )}
        handleAddAllocation={handleAddAllocation}
      />
    ),
    [
      handleAddResourceRequestClick,
      isResourceAssignmentEnabled,
      permittedActionUris,
      handleAddAllocation
    ]
  );

  return (
    <ChartRangeItem
      classes={cssClasses}
      leftComponentWidth={leftComponentWidth}
      leftComponent={leftComponent}
      scale={scale}
      scaleItemCount={chartDates.length}
      chartStartDate={chartDates.length && chartDates[0].start}
      rightComponent={total}
    />
  );
};

ResourceRequestChartFooter.propTypes = {
  chartDates: PropTypes.array.isRequired,
  scale: PropTypes.string.isRequired,
  leftComponentWidth: PropTypes.number.isRequired,
  project: PropTypes.object.isRequired,
  handleAddResourceRequestClick: PropTypes.func,
  handleAddAllocation: PropTypes.func
};

export default deepPure(ResourceRequestChartFooter);
